import React, { useCallback, useState } from "react";
import { t } from "ttag";
import { BaseDialog, Tabs, useConfirmation } from "@coolremote/shared";
import { useStoreActions } from "../../../../models/RootStore";
import DevicesLibraryTab from "./DevicesLibraryTab";
import FunctionalUnitsLibraryTab from "./FunctionalUnitsLibraryTab";
import ControlUiLibraryTab from "./ControlUiLibraryTab";
import CoolAutomationLibraryTab from "./CoolAutomationLibraryTab";
import { ILibrarySaveApi } from "./useLibraryTab";
import styles from "./ModbusSiteLibraryManagementDialog.module.css";

type LibraryTab = "devices" | "functionalUnits" | "controlUi" | "coolAutomationLibrary";

interface IProps {
  open: boolean;
  siteId?: string;
  onClose: () => void;
}

const ModbusSiteLibraryManagementDialog: React.FC<IProps> = ({ open, siteId, onClose }) => {
  const confirm = useConfirmation();
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const [tab, setTab] = useState<LibraryTab>("devices");

  /**
   * The save affordance reported by whichever tab is on screen (null for the
   * CoolAutomation Library tab, which has no staged edits of its own). Drives
   * the dialog's own footer Save button — there's no separate Close button,
   * since the window's own X already closes it.
   */
  const [saveApi, setSaveApi] = useState<ILibrarySaveApi | null>(null);

  /** Whether the CoolAutomation Library tab has a download in flight. */
  const [busy, setBusy] = useState(false);
  const handleBusyChange = useCallback((next: boolean) => {
    setBusy(next);
  }, []);

  const confirmDiscard = () =>
    confirm({
      title: t`Unsaved Changes`,
      bodyMessage: t`You have changes that have not been saved. Leave without saving?`,
      confirmText: t`Leave`,
      cancelText: t`Stay`,
    });

  const handleTabChange = async (value: string | number) => {
    const next = value as LibraryTab;
    if (next === tab) {
      return;
    }
    if (busy) {
      addMessage({ message: t`Please wait for the current download to finish before switching tabs.`, hideReportButton: true  });
      return;
    }
    // Switching tabs unmounts the current one and drops its staged edits, so
    // ask first rather than discarding silently.
    if (saveApi?.isDirty && !(await confirmDiscard())) {
      return;
    }
    setTab(next);
  };

  const handleClose = async () => {
    if (busy) {
      addMessage({ message: t`Please wait for the current download to finish before closing.`, hideReportButton: true  });
      return;
    }
    // Closing loses staged edits exactly like switching tabs does.
    if (saveApi?.isDirty && !(await confirmDiscard())) {
      return;
    }
    onClose();
  };

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      onConfirm={saveApi?.save}
      title={t`Modbus - Site Library Management`}
      hideCancel
      confirmLabel={t`Save`}
      confirmDisabled={!saveApi?.isDirty}
      loading={saveApi?.saving}
      confirmSize="medium"
      size="xlarge"
      staticBackdrop
    >
      <div className={styles.tabsWrap}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          tabs={[
            { value: "devices", label: t`Devices` },
            { value: "functionalUnits", label: t`Functional Units` },
            { value: "controlUi", label: t`Control UI` },
            { value: "coolAutomationLibrary", label: t`CoolAutomation Library` },
          ]}
        />
      </div>

      <div className={styles.tabPanel}>
        {tab === "devices" && <DevicesLibraryTab siteId={siteId} active={open && tab === "devices"} onSaveApiChange={setSaveApi} />}
        {tab === "functionalUnits" && <FunctionalUnitsLibraryTab siteId={siteId} active={open && tab === "functionalUnits"} onSaveApiChange={setSaveApi} />}
        {tab === "controlUi" && <ControlUiLibraryTab siteId={siteId} active={open && tab === "controlUi"} onSaveApiChange={setSaveApi} />}
        {tab === "coolAutomationLibrary" && (
          <CoolAutomationLibraryTab siteId={siteId} active={open && tab === "coolAutomationLibrary"} onBusyChange={handleBusyChange} />
        )}
      </div>
    </BaseDialog>
  );
};

export default ModbusSiteLibraryManagementDialog;
